/**
 * creativeWorkflow/metadataDomain.ts
 *
 * Pure domain functions for creative file metadata:
 * - parsing filenames into metadata dictionaries
 * - filling metadata with fallbacks
 * - building new filenames from metadata
 * - validating required fields and detecting duplicates
 *
 * These functions have ZERO Redux or side-effect dependencies.
 * They can be unit-tested in isolation.
 */

import fromPairs from 'lodash/fromPairs';
import groupBy from 'lodash/groupBy';
import splitFilename from '../../../general/utils/splitFilename';
import { spacesToDashes } from '../creativeFolder/model';
import type {
  CreativeBLSettings,
  PreparableFile,
  PreparedFile,
  ValidationIssue,
} from './contracts';

// Re-use existing model types for backward compat
import {
  Dictionary,
  DictionaryType,
  JobMeta,
  JobMode,
  MASK_PARAMS,
  specificFields,
} from './legacy_models/model';

import {
  Fallback,
  FallbackDictionary,
  fallbackIsPlainDictionary,
  getFallbackForNode,
  getForcedMetaForNode,
} from './legacy_models/fallback';

export interface MetadataDraftFile extends PreparableFile {
  meta: Record<string, string>;
}

export interface MetadataDraftValidationState {
  summary: string | null;
  issuesByFile: Record<string, ValidationIssue[]>;
  blockingFileIds: string[];
  blockingIssueCount: number;
}

export interface MetadataDraftAnalysis {
  newFilenames: Record<string, string>;
  validation: MetadataDraftValidationState;
}

function matchLanguageValue(token: string, values: string[]): string {
  const normalizedToken = token.trim().toLowerCase();
  if (!normalizedToken) return '';

  return values.find((value) => value.toLowerCase() === normalizedToken) ?? '';
}

// ─── tryParse ────────────────────────────────────────────────────────────────
// Attempt to parse a filename into metadata values based on dictionary mask.

export function tryParse(
  filename: string,
  settings: CreativeBLSettings,
  fallback: FallbackDictionary,
): Record<string, string> {
  const result: Record<string, string> = {};

  let restName = splitFilename(filename).baseName;
  restName = spacesToDashes(restName);

  const SEPARATOR = '_';

  const cutNextPart = () => {
    if (!restName.length) {
      return '';
    }
    const part = restName.split(SEPARATOR)[0];
    restName = restName.slice(part.length);
    if (restName.startsWith(SEPARATOR)) {
      restName = restName.slice(SEPARATOR.length);
    }
    return part;
  };

  settings.dictionaries.forEach((dict) => {
    if (dict.type === DictionaryType.srv) {
      if (dict.name === specificFields.SRV_CREATIVE_NODE_NAME) {
        const startsWithNodeName =
          restName.toLowerCase().indexOf(settings.creative.node_name.toLowerCase()) === 0;
        const nextChar = restName[settings.creative.node_name.length];
        if (startsWithNodeName && (nextChar === SEPARATOR || !nextChar)) {
          restName = restName.slice(settings.creative.node_name.length + nextChar.length);
        } else {
          // Consume one segment to keep the remaining mask aligned even if
          // the filename still contains a previous creative name.
          cutNextPart();
        }
        result[dict.name] = settings.creative.node_name;
      } else {
        cutNextPart();
        result[dict.name] = '';
      }
    } else if (dict.type === DictionaryType.lib) {
      if (dict.name === specificFields.LIB_PROJECT_NAME) {
        cutNextPart();
        result[dict.name] = settings.project.short_name;
      } else if (dict.name === specificFields.LIB_LANGUAGE_DICTIONARY) {
        const part = cutNextPart();
        result[dict.name] = matchLanguageValue(part, dict.values ?? []);
      } else {
        const part = cutNextPart();
        result[dict.name] =
          (dict.values ?? []).find((val) => part.toLowerCase() === val.toLowerCase()) ?? '';
      }
    } else if (dict.type === DictionaryType.str) {
      const part = cutNextPart();
      const maxLength = MASK_PARAMS[dict.name]?.maxLength;
      result[dict.name] = maxLength ? part.substring(0, maxLength) : part;
    }
  });

  Object.keys(result).forEach((name) => {
    if (!result[name] && fallback[name] !== undefined) {
      result[name] = fallback[name];
    }
  });

  // Fallback: find language (and other lib-dict values) from the filename using
  // "_XX_" or "_XX" (end) patterns, but only AFTER the creative node name to
  // avoid false matches inside the creative name itself.
  const normalizedBaseName = spacesToDashes(splitFilename(filename).baseName);
  const normalizedNodeName = spacesToDashes(settings.creative.node_name);
  const nodeNameIndex = normalizedBaseName.toLowerCase().indexOf(normalizedNodeName.toLowerCase());
  const searchFrom = nodeNameIndex >= 0 ? nodeNameIndex + normalizedNodeName.length : 0;
  const searchSubstring = normalizedBaseName.slice(searchFrom);

  const langTokenPattern = /_([A-Za-z]{2,5})(?=_|$)/g;
  const langTokens: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = langTokenPattern.exec(searchSubstring)) !== null) {
    langTokens.push(m[1]);
  }

  settings.dictionaries.forEach((dict) => {
    if (dict.type !== DictionaryType.lib) return;
    if (dict.name === specificFields.LIB_PROJECT_NAME) return;
    if (result[dict.name]) return;

    const values = dict.values ?? [];
    for (const token of langTokens) {
      const matchedVal =
        dict.name === specificFields.LIB_LANGUAGE_DICTIONARY
          ? matchLanguageValue(token, values)
          : values.find((val) => token.toLowerCase() === val.toLowerCase()) ?? '';
      if (matchedVal) {
        result[dict.name] = matchedVal;
        break;
      }
    }
  });

  return result;
}

// ─── fillMeta ────────────────────────────────────────────────────────────────
// Fill metadata for a batch of files based on mode, settings, and fallbacks.

export interface FileMetaInput {
  id: string;
  filename: string;
  nodeId?: number;
  existingMeta?: Record<string, string>;
}

export interface FileMetaOutput {
  fileId: string;
  meta: JobMeta;
}

export function fillMeta(
  mode: JobMode,
  settings: CreativeBLSettings,
  files: FileMetaInput[],
  fallback: Fallback = {},
): FileMetaOutput[] {
  const dictPairs = settings.dictionaries.map((maskItem) => [maskItem.name, '']);
  const blankMeta: Record<string, string> = fromPairs(dictPairs);

  return files.map((f) => {
    let nodeFallback: FallbackDictionary = {};
    let forcedMeta: FallbackDictionary = {};
    if (fallbackIsPlainDictionary(fallback)) {
      nodeFallback = fallback;
    } else if (f.nodeId) {
      nodeFallback = getFallbackForNode(fallback, f.nodeId);
      forcedMeta = getForcedMetaForNode(fallback, f.nodeId);
    }

    const nonEmptyForcedMeta = fromPairs(
      Object.entries(forcedMeta).filter(([, value]) => {
        if (value === undefined || value === null) return false;
        return String(value).trim() !== '';
      }),
    ) as Record<string, string>;

    const meta: Record<string, string> =
      mode === JobMode.UPDATE
        ? { ...tryParse(f.filename, settings, nodeFallback), ...(f.existingMeta || {}) }
        : { ...tryParse(f.filename, settings, nodeFallback), ...nonEmptyForcedMeta };

    return { fileId: f.id, meta: { ...blankMeta, ...meta } };
  });
}

export function prepareMetadataDraftFiles(
  files: PreparableFile[],
  settings: CreativeBLSettings,
  mode: JobMode = JobMode.UPLOAD,
  fallback: Fallback = {},
): MetadataDraftFile[] {
  const inputs = files.map((file) => ({
    id: file.id,
    filename: file.originalName,
    nodeId: file.nodeRef?.id,
  }));

  const filled = fillMeta(mode, settings, inputs, fallback);
  const metaById = new Map(filled.map((item) => [item.fileId, item.meta]));
  const blankMeta = fromPairs(settings.dictionaries.map((dict) => [dict.name, '']));

  return files.map((file) => ({
    ...file,
    meta: metaById.get(file.id) || blankMeta || {},
  }));
}

// ─── createNamePart ──────────────────────────────────────────────────────────
// Build a new filename from filled metadata + extension.

export function createNamePart(
  dictionaries: Dictionary[],
  meta: Record<string, string>,
  extension: string,
): string {
  if (!meta || !dictionaries) return '';
  const serialized = dictionaries
    .map((dict) => {
      if (meta[dict.name]) {
        return meta[dict.name];
      } else {
        if (dict.optional) {
          return undefined;
        }
        return '???';
      }
    })
    .filter((x) => x)
    .join('_');

  return `${serialized}.${extension}`;
}

// ─── Validation helpers ──────────────────────────────────────────────────────

/**
 * Check that all required metadata fields are filled for every file.
 */
export function allRequiredFieldsFilled(
  dictionaries: Dictionary[],
  filesMeta: Record<string, string>[],
): boolean {
  const requiredKeys = dictionaries.filter((d) => !d.optional).map((d) => d.name);
  return filesMeta.every((meta) =>
    requiredKeys.every((key) => meta[key] !== undefined && meta[key] !== ''),
  );
}

/**
 * Detect duplicate new filenames in a batch.
 * Returns the first duplicate filename, or null if none.
 */
export function findDuplicateFilename(filenames: string[]): string | null {
  const grouped = groupBy(filenames);
  const duplicate = Object.keys(grouped).find((key) => grouped[key].length > 1);
  return duplicate ?? null;
}

/**
 * Compute new filenames for a set of files based on their metadata.
 */
export function computeNewFilenames(
  files: { id: string; originalName: string; meta: Record<string, string> }[],
  dictionaries: Dictionary[],
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const f of files) {
    const ext = splitFilename(f.originalName).extension;
    result[f.id] = createNamePart(dictionaries, f.meta, ext);
  }
  return result;
}

export function validateMetadataDraftFiles(args: {
  files: MetadataDraftFile[];
  excludedFilesCount?: number;
  dictionaries: Dictionary[];
  occupiedFilenames?: string[];
  submitInProgress?: boolean;
}): MetadataDraftAnalysis {
  const {
    files,
    excludedFilesCount = 0,
    dictionaries,
    occupiedFilenames = [],
    submitInProgress = false,
  } = args;

  const newFilenames = computeNewFilenames(files, dictionaries);

  if (files.length === 0) {
    return {
      newFilenames,
      validation: {
        summary:
          excludedFilesCount > 0
            ? 'All items are currently excluded. Restore at least one item to continue.'
            : 'No files selected. Add at least one file to continue.',
        issuesByFile: {},
        blockingFileIds: [],
        blockingIssueCount: 1,
      },
    };
  }

  if (submitInProgress) {
    return {
      newFilenames,
      validation: {
        summary: 'Applying changes. Please wait until the current operation finishes.',
        issuesByFile: {},
        blockingFileIds: [],
        blockingIssueCount: 0,
      },
    };
  }

  const issuesByFile: Record<string, ValidationIssue[]> = {};
  const requiredDicts = dictionaries.filter((dictionary) => !dictionary.optional);
  const filenameGroups = new Map<string, string[]>();
  const occupiedFilenamesSet = new Set(occupiedFilenames);

  for (const file of files) {
    if (file.validationIssues?.length) {
      issuesByFile[file.id] = [...(issuesByFile[file.id] ?? []), ...file.validationIssues];
    }

    const missingRequiredFields = requiredDicts
      .filter(
        (dictionary) =>
          file.meta[dictionary.name] === undefined || file.meta[dictionary.name] === '',
      )
      .map((dictionary) => dictionary.header || dictionary.name);

    if (missingRequiredFields.length > 0) {
      issuesByFile[file.id] = [
        ...(issuesByFile[file.id] ?? []),
        {
          code: 'missing-required-fields',
          severity: 'error',
          fieldNames: missingRequiredFields,
          message: `Fill required fields: ${missingRequiredFields.join(', ')}`,
        },
      ];
    }

    const nextFilename = newFilenames[file.id];
    if (!nextFilename) continue;

    const fileIds = filenameGroups.get(nextFilename) ?? [];
    fileIds.push(file.id);
    filenameGroups.set(nextFilename, fileIds);
  }

  for (const [filename, fileIds] of filenameGroups) {
    if (fileIds.length < 2) continue;

    for (const fileId of fileIds) {
      issuesByFile[fileId] = [
        ...(issuesByFile[fileId] ?? []),
        {
          code: 'duplicate-filename',
          severity: 'error',
          relatedFileIds: fileIds.filter((id) => id !== fileId),
          message: `Resulting filename duplicates another row: ${filename}`,
        },
      ];
    }
  }

  for (const file of files) {
    const nextFilename = newFilenames[file.id];
    if (!nextFilename || !occupiedFilenamesSet.has(nextFilename)) continue;

    issuesByFile[file.id] = [
      ...(issuesByFile[file.id] ?? []),
      {
        code: 'occupied-filename',
        severity: 'error',
        conflictingFilename: nextFilename,
        message: `Resulting filename already exists in this folder: ${nextFilename}`,
      },
    ];
  }

  const allIssues = Object.values(issuesByFile).flat();
  const blockingFileIds = Object.entries(issuesByFile)
    .filter(([, issues]) => issues.some((issue) => issue.severity === 'error'))
    .map(([fileId]) => fileId);
  const filesWithIssues = Object.keys(issuesByFile).length;
  const duplicateIssueCount = allIssues.filter(
    (issue) => issue.code === 'duplicate-filename',
  ).length;
  const occupiedIssueCount = allIssues.filter((issue) => issue.code === 'occupied-filename').length;
  const missingIssueCount = allIssues.filter(
    (issue) => issue.code === 'missing-required-fields',
  ).length;

  let summary: string | null = null;
  if (blockingFileIds.length > 0) {
    const summaryParts: string[] = [];
    if (missingIssueCount > 0) {
      summaryParts.push('fill all required fields');
    }
    if (duplicateIssueCount > 0) {
      summaryParts.push('resolve duplicate resulting filenames');
    }
    if (occupiedIssueCount > 0) {
      summaryParts.push('choose filenames that are not already used in this folder');
    }
    const details = summaryParts.length > 0 ? ` To continue, ${summaryParts.join(' and ')}.` : '';
    summary = `${blockingFileIds.length} ${blockingFileIds.length === 1 ? 'file has' : 'files have'} blocking issues.${details}`;
  } else if (filesWithIssues > 0) {
    summary = `${filesWithIssues} ${filesWithIssues === 1 ? 'file has' : 'files have'} warnings.`;
  }

  return {
    newFilenames,
    validation: {
      summary,
      issuesByFile,
      blockingFileIds,
      blockingIssueCount: blockingFileIds.length,
    },
  };
}

export function buildPreparedFiles(
  files: MetadataDraftFile[],
  newFilenames: Record<string, string>,
): PreparedFile[] {
  return files.map((file) => ({
    id: file.id,
    originalName: file.originalName,
    relpath: file.relpath,
    pathSegments: file.pathSegments,
    validationIssues: file.validationIssues,
    excluded: file.excluded,
    exclusionReason: file.exclusionReason,
    fileRef: file.fileRef,
    nodeRef: file.nodeRef,
    newFilename: newFilenames[file.id] ?? file.originalName,
    meta: file.meta,
  }));
}
